import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { CATEGORIES } from "@/lib/categories";

const FILTERS = [{ value: "", label: "All" }, ...CATEGORIES];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  const sub = params.get("sub") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = cat ? `/products?category=${cat}` : "/products";
    api.get(url).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [cat]);

  // Unique sub-categories within the currently selected main category
  const subcats = useMemo(() => {
    const set = new Set();
    products.forEach((p) => { if (p.subcategory) set.add(p.subcategory); });
    return Array.from(set).sort();
  }, [products]);

  const visible = sub ? products.filter((p) => p.subcategory === sub) : products;

  const setCat = (value) => {
    if (!value) setParams({});
    else setParams({ cat: value });
  };
  const setSub = (value) => {
    if (!value) setParams({ cat });
    else setParams({ cat, sub: value });
  };

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <p className="smallcaps mb-3">The Catalog</p>
      <h1 className="font-display text-5xl sm:text-6xl text-[#212121] mb-12">Shop</h1>

      {/* Main category tabs */}
      <div className="flex gap-4 sm:gap-6 border-b border-[#B2DFDB] mb-6 overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
        {FILTERS.map((c) => (
          <button
            key={c.value}
            data-testid={`filter-${c.value || "all"}`}
            onClick={() => setCat(c.value)}
            className={`pb-3 text-sm uppercase tracking-widest transition-colors whitespace-nowrap ${
              cat === c.value ? "text-[#B87333] font-bold border-b-2 border-[#B87333] -mb-px" : "text-[#757575] hover:text-[#212121]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Sub-category chip row */}
      {cat && subcats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-12" data-testid="subcategory-row">
          <button
            onClick={() => setSub("")}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
              !sub ? "border-[#B87333] text-[#B87333] font-bold" : "border-[#B2DFDB] text-[#757575] hover:border-[#B87333]"
            }`}
            data-testid="sub-filter-all"
          >
            All
          </button>
          {subcats.map((s) => (
            <button
              key={s}
              onClick={() => setSub(s)}
              data-testid={`sub-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                sub === s ? "border-[#B87333] text-[#B87333] font-bold" : "border-[#B2DFDB] text-[#757575] hover:border-[#B87333]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-[#757575]">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-[#757575]" data-testid="shop-empty">No pieces in this category yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {visible.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
